import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Actions, ofType} from '@ngrx/effects';
import {
    AuthActions,
    ConverterService,
    OccEndpointsService,
    OccProductSearchAdapter,
    PRODUCT_SEARCH_PAGE_NORMALIZER,
    ProductSearchAdapter,
    UserIdService,
    withdrawOn,
} from '@spartacus/core';
import {Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, mergeMap, switchMap} from 'rxjs/operators';

import {AimoSearchConfig} from '../../../model/misc.model';
import {AimoProductSearchPage} from '../../../model/product.model';
import {AimoDeliveryDateService} from '../../routecalendar/aimo-delivery-date.service';
import {AimoUserService} from '../../user/aimo-user.service';
import {AimoAssortmentService} from '../aimo-assortment.service';
import {AimoRecentProductsService} from '../aimo-recent-proucts.service';
import {AimoSolrDebugInfoService} from '../aimo-solr-debug-info.service';

@Injectable({
    providedIn: 'root',
})
export class AimoOccProductSearchAdapter extends OccProductSearchAdapter implements ProductSearchAdapter {
    private contextChange$: Observable<never>;

    constructor(
        protected http: HttpClient,
        protected occEndpoints: OccEndpointsService,
        protected converter: ConverterService,
        protected userService: AimoUserService,
        protected userIdService: UserIdService,
        protected deliveryDateService: AimoDeliveryDateService,
        protected assortmentService: AimoAssortmentService,
        protected actions$: Actions,
        protected recentProductsService: AimoRecentProductsService,
        protected solrDebugInfoService: AimoSolrDebugInfoService
    ) {
        super(http, occEndpoints, converter);
        this.contextChange$ = this.actions$.pipe(ofType(AuthActions.LOGOUT));
    }

    search(query: string, searchConfig: AimoSearchConfig): Observable<AimoProductSearchPage> {
        return this.getSearchEndpoint2(query, searchConfig, 'productSearch').pipe(
            mergeMap((url) => {
                return this.http.get<AimoProductSearchPage>(url).pipe(
                    this.converter.pipeable(PRODUCT_SEARCH_PAGE_NORMALIZER),
                    map(
                        (searchResult) =>
                            ({
                                ...searchResult,
                                cartSearchMode: searchConfig.cartSearchMode,
                            }) as AimoProductSearchPage,
                    ),
                );
            }),
            catchError((err) => {
                // eslint-disable-next-line
                console.error(err);
                return of();
            }),
        );
    }

    excelExportProducts(query: string, searchConfig: AimoSearchConfig): Observable<ArrayBuffer> {
        return this.getSearchEndpoint2(query, searchConfig, 'excelExportProducts').pipe(
            mergeMap((url) =>
                // eslint-disable-next-line
                this.http.get<ArrayBuffer>(url, {responseType: 'arraybuffer' as any}),
            ),
        );
    }

    safetyDataExport(code: string): Observable<ArrayBuffer> {
        const url = this.occEndpoints.buildUrl('safetyDataExport', {
            urlParams: {code},
        });
        return this.http.get<ArrayBuffer>(url, {responseType: 'arraybuffer' as any});
    }

    protected getSearchEndpoint2(query: string, searchConfig: AimoSearchConfig, endPoint: string): Observable<string> {
        let previousDate: string;
        let previousMyAssortment: boolean;
        return this.solrDebugInfoService.getSolrDebugInfo().pipe(
            distinctUntilChanged((prev, curr) => prev === curr),
            switchMap((solrDebugMode) =>
                this.assortmentService.isOnlyMyAssortment().pipe(
                    distinctUntilChanged((prev, curr) => prev === curr),
                    switchMap((onlyMyAssortment) =>
                        this.deliveryDateService.getDeliveryDate().pipe(
                            distinctUntilChanged((prev, curr) => prev === curr),
                            filter(Boolean),
                            map((requestedDeliveryDate) => {
                                const resetSearch =
                                    (previousDate && previousDate !== requestedDeliveryDate) || // reset search paging on deliverydate change
                                    (previousMyAssortment && previousMyAssortment !== onlyMyAssortment); // reset search paging on assortment switch
                                const url = this.occEndpoints.buildUrl(endPoint, {
                                    queryParams: {
                                        query,
                                        ...searchConfig,
                                        resetSearch,
                                        requestedDeliveryDate,
                                        onlyMyAssortment,
                                        solrDebugMode,
                                        recentProducts: this.recentProductsService.get(30),
                                    },
                                });
                                previousDate = requestedDeliveryDate;
                                previousMyAssortment = onlyMyAssortment;
                                return url;
                            }),
                        ),
                    ),
                    withdrawOn(this.contextChange$),
                )
            ),
            distinctUntilChanged((prev, curr) => prev === curr)
        );
    }


}
